import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Target, Mic, TrendingUp, ChevronDown, LineChart as LineChartIcon, MessageSquare, Bookmark } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../lib/api";
import { CategoryPerformance } from "../CategoryPerformance";
import { DeliveryTrend } from "../interview/DeliveryTrend";

function Ring({ value }) {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20 shrink-0">
      <svg viewBox="0 0 76 76" className="w-20 h-20 -rotate-90">
        <circle cx="38" cy="38" r={r} stroke="#e2e8f0" strokeWidth="8" fill="none" />
        <motion.circle cx="38" cy="38" r={r} stroke="#DC2626" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (value / 100) * c }} transition={{ duration: 1, ease: "easeOut" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-heading text-2xl font-extrabold text-slate-900" data-testid="readiness-score">{value}</div>
    </div>
  );
}

export function useDashboardData() {
  const [stats, setStats] = useState(null);
  const [examHistory, setExamHistory] = useState([]);
  const [interviews, setInterviews] = useState([]);
  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/exam/history").then(({ data }) => setExamHistory(data.history || [])).catch(() => {});
    api.get("/interview/history").then(({ data }) => setInterviews((data.interviews || []).filter((i) => i.status === "completed" && i.scores && i.scores.overall != null))).catch(() => {});
  }, []);
  return { stats, examHistory, interviews };
}

export function StatsStrip({ stats, bookmarks }) {
  const navigate = useNavigate();
  if (!stats) return null;
  const tiles = [
    [Target, "Questions", stats.total_questions_answered, "stat-questions"],
    [Award, "Accuracy", `${stats.quiz_accuracy}%`, "stat-accuracy"],
    [Mic, "Boards", stats.interviews_completed, "stat-boards"],
    [TrendingUp, "Avg board", stats.avg_interview_score, "stat-avg-board"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-6" data-testid="stats-strip">
      <div className="flex items-center gap-4">
        <Ring value={stats.readiness_score ?? stats.readiness ?? 0} />
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Readiness</p>
          <p className="text-sm text-slate-600 max-w-[16rem]">Half question accuracy, half oral-board average.</p>
        </div>
      </div>
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 md:border-l md:border-slate-100 md:pl-6">
        {tiles.map(([Icon, l, v, id]) => (
          <div key={l} data-testid={id}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Icon className="w-3.5 h-3.5 text-primary" /> {l}</p>
            <p className="font-heading text-2xl font-bold text-slate-900">{v}</p>
          </div>
        ))}
      </div>
      <button onClick={() => navigate("/bookmarks")} className="text-sm text-slate-600 hover:text-primary inline-flex items-center gap-1.5 shrink-0" data-testid="view-bookmarks-button">
        <Bookmark className="w-4 h-4" /> Bookmarks{bookmarks != null ? ` (${bookmarks})` : ""}
      </button>
    </div>
  );
}

function ExamHistoryChart({ history }) {
  if (!history.length) return null;
  const data = history.map((e, i) => ({ name: `#${i + 1}`, score: e.score }));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="exam-history-section">
      <div className="flex items-center gap-2 mb-4"><LineChartIcon className="w-5 h-5 text-primary" /><h3 className="font-heading text-xl text-slate-900">Timed Exam History</h3></div>
      <div className="h-52 min-h-[208px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
            <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} formatter={(v) => [`${v}%`, "Score"]} />
            <Line type="monotone" dataKey="score" stroke="#DC2626" strokeWidth={3} dot={{ r: 4, fill: "#DC2626" }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-slate-500 mt-2">Last {history.length} timed exam{history.length > 1 ? "s" : ""} · pass mark 70%.</p>
    </div>
  );
}

function InterviewReplays({ interviews }) {
  // Compact overview only — the full report, transcript and pacing live in Mock Oral Board → Results.
  const navigate = useNavigate();
  if (!interviews.length) return null;
  const recent = interviews.slice(0, 3);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="interview-replay-section">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /><h3 className="font-heading text-xl text-slate-900">Recent Mock Oral Boards</h3></div>
        <button type="button" onClick={() => navigate("/interview?tab=results")} className="text-xs font-semibold text-primary hover:underline" data-testid="dashboard-view-results">View all results →</button>
      </div>
      <div className="space-y-2">
        {recent.map((iv, i) => {
          const ov = iv.scores.overall;
          const badge = ov >= 70 ? "bg-green-100 text-green-700" : ov >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";
          return (
            <button key={iv.id} type="button" onClick={() => navigate(`/interview?tab=results&result=${iv.id}`)} className="w-full flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 hover:bg-slate-50 text-left" data-testid={`interview-replay-${i}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-heading font-bold ${badge}`}>{ov}</div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{iv.department?.name ? `Department focus · ${iv.department.name}` : "General board"}</p>
                  <p className="text-xs text-slate-400">{new Date(iv.completed_at || iv.created_at).toLocaleDateString()}{iv.scores.pacing?.duration_label ? ` · ${iv.scores.pacing.duration_label}` : ""}</p>
                </div>
              </div>
              <span className="text-xs font-semibold text-slate-500">Report →</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function PracticeProgress({ stats, examHistory, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const has = (stats?.categories?.length > 0) || examHistory.length > 0;
  if (!has) return null;
  return (
    <div className="mt-2" data-testid="progress-panels">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-5 py-4 hover:bg-slate-50" data-testid="progress-toggle">
        <span className="font-heading text-lg text-slate-900">Practice progress <span className="text-sm text-slate-400 font-body ml-2">performance by section · exam history</span></span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-6 mt-4">
          {stats?.categories?.length > 0 && <CategoryPerformance categories={stats.categories} />}
          <ExamHistoryChart history={examHistory} />
        </div>
      )}
    </div>
  );
}

export function BoardProgress({ interviews }) {
  if (!interviews.length) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="board-progress">
      <InterviewReplays interviews={interviews} />
      {interviews.length > 1 ? <DeliveryTrend interviews={interviews} /> : (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-6 flex items-center justify-center text-sm text-slate-500">Complete one more board to unlock your delivery trend.</div>
      )}
    </div>
  );
}
